import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import DataTable from './DataTable'
import SnapshotBadge from './SnapshotBadge'
import { buildParityHoverSummary } from '../../utils/snoop/parityHover'
import { usePresentExpand } from '../../hooks/usePresentExpand'

function PeakLegend() {
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block h-3 w-3 rounded" style={{ backgroundColor: '#90EE90' }} />
        90D peak
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block h-3 w-3 rounded" style={{ backgroundColor: '#FFD700' }} />
        All-time peak
      </span>
    </div>
  )
}

const btnClass =
  'inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-800 shadow-sm transition hover:border-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60'

function ParityReconcileButton({
  onClick,
  disabled,
  refreshing,
  label,
  parity,
  className = btnClass,
}) {
  const summary = useMemo(() => buildParityHoverSummary(parity), [parity])
  const [open, setOpen] = useState(false)
  const hideTimer = useRef(null)
  const rootRef = useRef(null)
  const tipId = useId()

  function clearHideTimer() {
    if (hideTimer.current) {
      window.clearTimeout(hideTimer.current)
      hideTimer.current = null
    }
  }

  function showPanel() {
    clearHideTimer()
    setOpen(true)
  }

  function scheduleHide() {
    clearHideTimer()
    hideTimer.current = window.setTimeout(() => setOpen(false), 200)
  }

  useEffect(() => {
    return () => clearHideTimer()
  }, [])

  useEffect(() => {
    if (!open) return undefined
    function onDocMouseDown(event) {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false)
      }
    }
    function onKey(event) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocMouseDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div
      className="relative inline-flex"
      ref={rootRef}
      onMouseEnter={showPanel}
      onMouseLeave={scheduleHide}
      onFocus={showPanel}
      onBlur={(event) => {
        if (!rootRef.current?.contains(event.relatedTarget)) scheduleHide()
      }}
    >
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={className}
        aria-describedby={tipId}
        aria-expanded={open}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          className={refreshing ? 'animate-spin' : undefined}
        >
          <path
            d="M4 12a8 8 0 0 1 13.66-5.66M20 12a8 8 0 0 1-13.66 5.66"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M17 4v4h4M7 20v-4H3"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {refreshing ? 'Aligning…' : label}
      </button>
      {open ? (
        <div
          id={tipId}
          role="tooltip"
          className="absolute right-0 top-full z-30 mt-1 w-[min(22rem,calc(100vw-2rem))] rounded-xl border border-gray-200 bg-white p-3 text-left text-xs text-gray-700 shadow-lg"
          onMouseEnter={showPanel}
          onMouseLeave={scheduleHide}
        >
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Align preview
          </p>
          <div className="max-h-64 space-y-2.5 overflow-y-auto overscroll-contain pr-1">
            {summary.lines.map((block) => (
              <div key={block.heading}>
                <p className="font-semibold text-gray-900">{block.heading}</p>
                <pre className="mt-0.5 whitespace-pre-wrap break-words font-sans text-[11px] leading-relaxed text-gray-600">
                  {block.body}
                </pre>
              </div>
            ))}
          </div>
          <p className="mt-2 border-t border-gray-100 pt-2 text-[10px] text-gray-400">
            Align upserts into Postgres; DB-only rows are left in place.
          </p>
        </div>
      ) : null}
    </div>
  )
}

export default function SummaryDashboard({
  title,
  subtitle,
  fetchData,
  onRefreshFromSheets = null,
  refreshLabel = 'Refresh mapping',
  onReconcileFromSheets = null,
  reconcileLabel = 'Align Postgres',
  onExportHtml = null,
  exportLabel = 'Export HTML',
  defaultSort,
  searchPlaceholder,
  searchActions = null,
  editableStatus = null,
  editablePlatforms = null,
  /** Pencil control for pub_mappings free-text/money fields: { onSave(payload) }. */
  editablePubMapping = null,
  onDeleteRow = null,
  hiddenColumns = [],
  truncateColumns = ['IG Full Name'],
  enablePeakHighlight = false,
  showPeakLegend = false,
  enableEngagementHighlight = false,
  layout = 'unsigned',
  linkLabel = 'IG',
  extraActions = null,
  addForm = null,
  tableKey = null,
  onSnoopSourcesAndMultiChange = null,
  enableHideNonEnglish = false,
  defaultHideNonEnglish = true,
  hideNonEnglish = undefined,
  onHideNonEnglishChange = null,
  showHideNonEnglishToggle = true,
  hideNonEnglishColumns = null,
  enableAnalyzeLink = false,
  analyzeRequireArtistMapping = false,
  enableGenreStartsWithFilter = false,
  genreColumn = 'Luminate Genres',
  enableCountryFilter = false,
  countryColumn = 'Country of Origin',
  enableStatusFilter = false,
  statusColumn = 'Status',
  statusFilterPreferred = null,
  enableRosterScopeFilter = false,
  onActiveFiltersChange = null,
  stickyHeader = true,
  /** Expand a parent row to reveal child rows held on the row (Assets tracks). */
  expandableNestedRows = false,
  nestedRowsKey = '_tracks',
  nestedExpandTitle = 'Show tracks',
  nestedRowClassName = 'bg-sky-50/90 text-slate-700',
  /** Second-level expand on each nested child (Assets track -> credits). */
  renderNestedDetail = null,
  nestedDetailKey = '_release_folder_id',
  nestedDetailTitle = 'Show credits',
  /** Per-row action buttons in a trailing column. */
  rowActions = null,
  expandableHistory = false,
  historyFetcher = null,
  renderExpandedHistory = null,
  historyExpandTitle = 'Show history',
  historyLoadingLabel = 'Loading history…',
  stackPctChgUnderStreams = false,
  serverSearch = false,
  onServerQueryChange = null,
  serverPagination = null,
  /**
   * When set, Next on the last client page fetches/appends more rows (progressive
   * chunking). Callback receives current payload; return { rows, truncated, total_matched }.
   */
  onProgressiveLoadMore = null,
  /** Per-page header renames: { [columnName]: 'Short Label' }. */
  headerLabels = null,
  /** Larger type for screen sharing (PGP Agenda "Present mode"). */
  presentMode = false,
  /** Tint rows where this column is truthy (PGP Charts "New This Week"). */
  rowHighlightColumn = null,
  rowHighlightClass = 'bg-violet-50',
  /** Comparator for the Status column: `(a, b) => number` (PGP pipeline order). */
  statusCompare = null,
  /** Relative column widths `{ [columnName]: weight }` (PGP Agenda: Notes widest). */
  columnWeights = null,
  /** Columns that wrap onto multiple lines instead of nowrap/truncate. */
  wrapColumns = [],
  /** Grey a row when this hidden column is truthy (Catalog taken-down). */
  muteRowKey = null,
  /**
   * Columns to render headers from before the first payload arrives, so the
   * table does not jump from empty to full width.
   */
  placeholderColumns = null,
}) {
  const [state, setState] = useState({ loading: true, error: null, data: null })
  const [statusBanner, setStatusBanner] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [refreshBanner, setRefreshBanner] = useState(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const hasLoadedRef = useRef(false)
  const presentExpand = usePresentExpand(presentMode)

  const load = useCallback(
    ({ soft = false } = {}) => {
      setState((prev) =>
        soft && prev.data
          ? { ...prev, loading: true, error: null }
          : { loading: true, error: null, data: null },
      )
      return fetchData()
        .then((data) => {
          hasLoadedRef.current = true
          setState({ loading: false, error: null, data })
        })
        .catch((error) => {
          setState((prev) =>
            soft && prev.data
              ? { ...prev, loading: false, error: error.message }
              : { loading: false, error: error.message, data: null },
          )
        })
    },
    [fetchData],
  )

  useEffect(() => {
    let cancelled = false
    // On fetchData change (snoop filters / search→min_new_follows, etc.): clear rows so we
    // do not flash the wrong cohort, but keep the payload shell so DataTable stays mounted
    // (filter + search state live there). Soft keep-previous rows is only load({ soft: true }).
    setLoadingMore(false)
    setState((prev) => {
      if (prev.data) {
        return {
          loading: true,
          error: null,
          data: {
            ...prev.data,
            rows: [],
            row_count: 0,
            truncated: false,
          },
        }
      }
      return { loading: true, error: null, data: null }
    })

    fetchData()
      .then((data) => {
        if (!cancelled) {
          hasLoadedRef.current = true
          setState({ loading: false, error: null, data })
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState((prev) =>
            prev.data
              ? { ...prev, loading: false, error: error.message }
              : { loading: false, error: error.message, data: null },
          )
        }
      })

    return () => {
      cancelled = true
    }
  }, [fetchData])

  const handleNeedMoreRows = useCallback(async () => {
    if (!onProgressiveLoadMore || loadingMore) return false
    const current = state.data
    if (!current?.truncated) return false
    setLoadingMore(true)
    setStatusBanner(null)
    try {
      const next = await onProgressiveLoadMore(current)
      const incoming = Array.isArray(next?.rows) ? next.rows : []
      const seen = new Set()
      for (const row of current.rows || []) {
        const id = row?.id
        if (id != null && String(id).trim() !== '') seen.add(String(id))
      }
      const appended = []
      for (const row of incoming) {
        const id = row?.id
        const key = id != null ? String(id) : ''
        if (key && seen.has(key)) continue
        if (key) seen.add(key)
        appended.push(row)
      }
      setState((prev) => {
        if (!prev.data) return prev
        const rows = [...(prev.data.rows || []), ...appended]
        return {
          ...prev,
          data: {
            ...prev.data,
            rows,
            row_count: rows.length,
            truncated: Boolean(next?.truncated),
            total_matched:
              next?.total_matched != null
                ? next.total_matched
                : prev.data.total_matched,
          },
        }
      })
      return appended.length > 0
    } catch (error) {
      setStatusBanner(error?.message || 'Failed to load more rows')
      return false
    } finally {
      setLoadingMore(false)
    }
  }, [onProgressiveLoadMore, loadingMore, state.data])

  async function handleRefresh() {
    if (!onRefreshFromSheets || refreshing) return
    setRefreshing(true)
    setRefreshBanner(null)
    setStatusBanner(null)
    try {
      await onRefreshFromSheets()
      setRefreshBanner({
        tone: 'ok',
        text: 'Synced snoop mapping and summary. Reloading…',
      })
      await load({ soft: true })
      setRefreshBanner({
        tone: 'ok',
        text: 'Synced snoop mapping and summary.',
      })
    } catch (error) {
      setRefreshBanner({
        tone: 'error',
        text: error?.message || 'Refresh failed',
      })
    } finally {
      setRefreshing(false)
    }
  }

  async function handleReconcile() {
    if (!onReconcileFromSheets || refreshing) return
    setRefreshing(true)
    setRefreshBanner(null)
    setStatusBanner(null)
    try {
      const result = await onReconcileFromSheets()
      setRefreshBanner({
        tone: 'ok',
        text: 'Aligned Postgres. Reloading…',
      })
      await load({ soft: true })
      const ok = result?.ok !== false
      setRefreshBanner({
        tone: ok ? 'ok' : 'error',
        text: ok
          ? 'Aligned Postgres.'
          : result?.summary?.error || 'Reconcile finished with errors — check API logs.',
      })
    } catch (error) {
      setRefreshBanner({
        tone: 'error',
        text: error?.message || 'Align failed',
      })
    } finally {
      setRefreshing(false)
    }
  }

  async function handleExport() {
    if (!onExportHtml || exporting) return
    setExporting(true)
    setRefreshBanner(null)
    try {
      await onExportHtml()
      setRefreshBanner({
        tone: 'ok',
        text: 'Opened email-style HTML (New Follows ≥ 2) in a new tab.',
      })
    } catch (error) {
      setRefreshBanner({
        tone: 'error',
        text: error?.message || 'Export failed',
      })
    } finally {
      setExporting(false)
    }
  }

  const { loading, error, data } = state
  // Mapping APIs report filters.sheet_io_enabled when sheet Align is gated.
  // Hide until payload arrives so frozen tables never flash the button.
  const sheetIoEnabled = data?.filters?.sheet_io_enabled
  const showReconcile =
    Boolean(onReconcileFromSheets) && data != null && sheetIoEnabled !== false
  const showActions = Boolean(
    onRefreshFromSheets || onExportHtml || showReconcile || extraActions,
  )
  const parity = data?.parity
  const parityComparable = parity != null && parity.aligned != null
  const parityOff = Boolean(parityComparable && parity.aligned === false)
  const loadedCount = data?.rows?.length ?? data?.row_count ?? 0
  const totalCount = data?.total_count

  const serverPaginationFooter = serverPagination
    ? (() => {
        const truncated =
          serverPagination.truncated ?? Boolean(data?.truncated)
        const totalMatched =
          serverPagination.totalMatched ?? data?.total_matched
        if (serverPagination.offset <= 0 && !truncated) return null
        return (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 shadow-sm">
            <button
              type="button"
              className={btnClass}
              disabled={loading || serverPagination.offset <= 0}
              onClick={() =>
                serverPagination.onOffsetChange(
                  Math.max(0, serverPagination.offset - serverPagination.limit),
                )
              }
            >
              Previous page (server)
            </button>
            <span className="text-center text-xs text-gray-600">
              Server window{' '}
              <span className="font-medium text-gray-900">
                {serverPagination.offset + 1}–
                {serverPagination.offset + loadedCount}
              </span>
              {totalMatched != null ? (
                <>
                  {' '}
                  of <span className="font-medium text-gray-900">{totalMatched}</span>{' '}
                  matched
                </>
              ) : null}
            </span>
            <button
              type="button"
              className={btnClass}
              disabled={loading || !truncated}
              onClick={() =>
                serverPagination.onOffsetChange(
                  serverPagination.offset + serverPagination.limit,
                )
              }
            >
              Next page (server)
            </button>
          </div>
        )
      })()
    : null

  return (
    <div
      className={[
        'space-y-6',
        // Break out of the page's max-w container so the table spans the
        // window. Only on viewports wide enough that it actually helps.
        presentMode && presentExpand
          ? 'relative left-1/2 w-screen -translate-x-1/2 px-4 md:px-6'
          : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <header className="space-y-3">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 md:text-4xl">{title}</h1>
          {showActions ? (
            <div className="flex shrink-0 flex-wrap items-center gap-2 sm:pt-1">
              {extraActions}
              {onExportHtml ? (
                <button
                  type="button"
                  onClick={handleExport}
                  disabled={exporting || loading}
                  className={btnClass}
                  title="Open email-parity HTML table (New Follows ≥ 2) for copy/paste"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M8 5h8a2 2 0 0 1 2 2v12l-6-3-6 3V7a2 2 0 0 1 2-2Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {exporting ? 'Exporting…' : exportLabel}
                </button>
              ) : null}
              {showReconcile ? (
                <ParityReconcileButton
                  onClick={handleReconcile}
                  disabled={refreshing || loading}
                  refreshing={refreshing}
                  label={reconcileLabel}
                  parity={parity}
                />
              ) : null}
              {onRefreshFromSheets ? (
                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={refreshing || loading}
                  className={btnClass}
                  title="Upsert snoop mapping and summary into Postgres"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                    className={refreshing ? 'animate-spin' : undefined}
                  >
                    <path
                      d="M4 12a8 8 0 0 1 13.66-5.66M20 12a8 8 0 0 1-13.66 5.66"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M17 4v4h4M7 20v-4H3"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {refreshing ? 'Refreshing…' : refreshLabel}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
        {data ? (
          <SnapshotBadge
            snapshotDate={data.snapshot_date}
            rowCount={loadedCount}
            totalCount={totalCount}
            scope={data.scope}
            filters={data.filters}
            parity={parity}
            source={data.source}
          />
        ) : null}
      </header>

      {data?.source === 'live_fallback' ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <p className="font-semibold">Using live compute fallback</p>
          <p className="mt-1 text-amber-900/90">
            Precomputed <code className="rounded bg-white px-1.5 py-0.5">luminate_track_summary</code> had
            no rows for this week — Residual was fitted on the fly. Refresh the Luminate summary pipeline
            for faster loads.
          </p>
        </div>
      ) : null}

      {parityOff ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <p className="font-semibold">Mapping drift detected</p>
          <p className="mt-1 text-amber-900/90">
            {parity.message || 'External mirror and Postgres differ.'} Mirror-only:{' '}
            <span className="font-medium">{parity.sheet_only_count ?? 0}</span>
            {' · '}DB-only: <span className="font-medium">{parity.db_only_count ?? 0}</span>
            {parity.status_column || (parity.status_mismatch_count ?? 0) > 0 ? (
              <>
                {' · '}
                {parity.mismatch_label || 'Value'} mismatches:{' '}
                <span className="font-medium">{parity.status_mismatch_count ?? 0}</span>
              </>
            ) : null}
            {' · '}Mirror rows: <span className="font-medium">{parity.sheet_row_count ?? 0}</span>
            {' · '}DB rows: <span className="font-medium">{parity.db_row_count ?? 0}</span>
          </p>
          {showReconcile ? (
            <ParityReconcileButton
              onClick={handleReconcile}
              disabled={refreshing || loading}
              refreshing={refreshing}
              label={reconcileLabel}
              parity={parity}
              className="mt-3 inline-flex items-center rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-950 hover:bg-amber-100 disabled:opacity-60"
            />
          ) : null}
        </div>
      ) : null}

      {refreshBanner ? (
        <div
          className={
            refreshBanner.tone === 'ok'
              ? 'rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900'
              : 'rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800'
          }
        >
          {refreshBanner.text}
        </div>
      ) : null}

      {statusBanner ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {statusBanner}
        </div>
      ) : null}

      {addForm ? <div>{addForm}</div> : null}

      {loading && !data ? (
        placeholderColumns?.length ? (
          // Headers-before-rows: keeps column widths stable on first load.
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr>
                  {placeholderColumns.map((column) => (
                    <th
                      key={column}
                      className="border-b border-gray-200 px-3 py-2.5 text-xs font-medium uppercase tracking-wide text-gray-400"
                    >
                      {headerLabels?.[column] || column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td
                    colSpan={placeholderColumns.length}
                    className="px-3 py-10 text-center text-gray-500"
                  >
                    Loading…
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-xl border border-gray-200 bg-white p-10 text-center text-gray-500 shadow-sm">
            Loading…
          </div>
        )
      ) : null}

      {loading && data ? (
        <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-2 text-xs text-blue-900">
          {(data.rows || []).length ? 'Updating rows…' : 'Loading filtered rows…'}
        </div>
      ) : null}

      {loadingMore ? (
        <div className="rounded-xl border border-sky-100 bg-sky-50 px-4 py-2 text-xs text-sky-900">
          Loading more rows…
        </div>
      ) : null}

      {error && !data ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-800 shadow-sm">
          <p className="font-semibold">Could not load dashboard data</p>
          <p className="mt-2 text-sm text-red-700/90">{error}</p>
          <p className="mt-4 text-sm text-gray-600">
            Make sure <code className="rounded bg-white px-1.5 py-0.5 text-gray-800">DATABASE_URL</code> is
            set, the DB tunnel is running if needed, and the API is up on port 8000.
          </p>
        </div>
      ) : null}

      {error && data ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          Refresh failed: {error}
        </div>
      ) : null}

      {data ? (
        <DataTable
          key={tableKey ?? layout}
          rows={data.rows || []}
          columns={data.columns || []}
          percentColumns={data.percent_columns}
          defaultSort={defaultSort}
          searchPlaceholder={searchPlaceholder}
          searchActions={searchActions}
          editableStatus={editableStatus}
          editablePlatforms={editablePlatforms}
          editablePubMapping={editablePubMapping}
          onDeleteRow={onDeleteRow}
          snapshotDate={data.snapshot_date}
          onStatusError={(msg) => setStatusBanner(msg)}
          hiddenColumns={hiddenColumns}
          truncateColumns={truncateColumns}
          enablePeakHighlight={enablePeakHighlight}
          enableEngagementHighlight={enableEngagementHighlight}
          legend={showPeakLegend ? <PeakLegend /> : null}
          layout={layout}
          linkLabel={linkLabel}
          onSnoopSourcesAndMultiChange={onSnoopSourcesAndMultiChange}
          snoopSourceOptions={data.snoop_source_options}
          serverSearch={serverSearch}
          onServerQueryChange={onServerQueryChange}
          truncated={Boolean(data.truncated)}
          totalMatched={data.total_matched}
          canLoadMore={Boolean(onProgressiveLoadMore && data.truncated)}
          onNeedMoreRows={onProgressiveLoadMore ? handleNeedMoreRows : null}
          loadingMore={loadingMore}
          loading={loading}
          enableHideNonEnglish={enableHideNonEnglish}
          defaultHideNonEnglish={defaultHideNonEnglish}
          hideNonEnglish={hideNonEnglish}
          onHideNonEnglishChange={onHideNonEnglishChange}
          showHideNonEnglishToggle={showHideNonEnglishToggle}
          hideNonEnglishColumns={hideNonEnglishColumns}
          enableAnalyzeLink={enableAnalyzeLink}
          analyzeRequireArtistMapping={analyzeRequireArtistMapping}
          enableGenreStartsWithFilter={enableGenreStartsWithFilter}
          genreColumn={genreColumn}
          enableCountryFilter={enableCountryFilter}
          countryColumn={countryColumn}
          enableStatusFilter={enableStatusFilter}
          statusColumn={statusColumn}
          statusFilterPreferred={statusFilterPreferred}
          enableRosterScopeFilter={enableRosterScopeFilter}
          onActiveFiltersChange={onActiveFiltersChange}
          stickyHeader={stickyHeader}
          expandableNestedRows={expandableNestedRows}
          nestedRowsKey={nestedRowsKey}
          nestedExpandTitle={nestedExpandTitle}
          nestedRowClassName={nestedRowClassName}
          renderNestedDetail={renderNestedDetail}
          nestedDetailKey={nestedDetailKey}
          nestedDetailTitle={nestedDetailTitle}
          rowActions={rowActions}
          expandableHistory={expandableHistory}
          historyFetcher={historyFetcher}
          renderExpandedHistory={renderExpandedHistory}
          historyExpandTitle={historyExpandTitle}
          historyLoadingLabel={historyLoadingLabel}
          stackPctChgUnderStreams={stackPctChgUnderStreams}
          headerLabels={headerLabels}
          presentMode={presentMode}
          presentExpand={presentExpand}
          rowHighlightColumn={rowHighlightColumn}
          rowHighlightClass={rowHighlightClass}
          statusCompare={statusCompare}
          columnWeights={columnWeights}
          wrapColumns={wrapColumns}
          muteRowKey={muteRowKey}
        />
      ) : null}

      {serverPaginationFooter}
    </div>
  )
}
